<script setup>
import { clearError } from '#app'
const WrHeader = defineAsyncComponent(() =>
    import('~/components/molecules/WrHeader.vue')
)
const WrFooter = defineAsyncComponent(() =>
    import('~/components/molecules/WrFooter.vue')
)
const WrOfficialPartnerHeader = defineAsyncComponent(() =>
    import('~/components/molecules/WrOfficialPartnerHeader.vue')
)
// This file should be placed in the root of your project (same level as app.vue)
const props = defineProps({
  error: Object
})

// Handle different error types
const is404 = computed(() => props.error?.statusCode === 404)
const is500 = computed(() => props.error?.statusCode === 500)

// Clear error and navigate
const handleErrorCleared = async () => {
  await clearError({ redirect: '/' })
}

// SEO
useHead({
  title: is404.value ? '404 - Page Not Found' : 'Error - Something went wrong',
  meta: [
    {
      name: 'robots',
      content: 'noindex'
    }
  ]
})
</script>

<template>
   <WrOfficialPartnerHeader />
      <WrHeader />
  <div>
    <!-- 404 Error -->
    <div v-if="is404">
      <div class="min-h-screen bg-gradient-to-br max-md:pt-18 pt-24 from-slate-900 via-purple-900 to-indigo-900 overflow-hidden relative">
        <!-- Background Pattern -->
        <div class="absolute inset-0 opacity-10">
          <div class="absolute inset-0" style="background-image: radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0); background-size: 50px 50px;"></div>
        </div>

        <div class="relative z-10 max-w-6xl mx-auto px-4 min-h-screen flex items-center">
          <div class="w-full text-center">
            <!-- 404 Illustration -->
            <div class="mb-8 flex justify-center">
              <svg class="w-80 h-64 lg:w-96 lg:h-80" viewBox="0 0 400 320" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- Astronaut floating in space -->
                <g>
                  <!-- Astronaut body -->
                  <ellipse cx="200" cy="180" rx="45" ry="60" fill="url(#astronautGradient)"/>
                  <!-- Helmet -->
                  <circle cx="200" cy="120" r="35" fill="url(#helmetGradient)" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>
                  <!-- Helmet reflection -->
                  <ellipse cx="190" cy="110" rx="15" ry="20" fill="rgba(255,255,255,0.4)"/>
                  <!-- Arms -->
                  <ellipse cx="160" cy="160" rx="12" ry="25" fill="url(#astronautGradient)" transform="rotate(-20 160 160)"/>
                  <ellipse cx="240" cy="160" rx="12" ry="25" fill="url(#astronautGradient)" transform="rotate(20 240 160)"/>
                  <!-- Legs -->
                  <ellipse cx="185" cy="230" rx="12" ry="30" fill="url(#astronautGradient)"/>
                  <ellipse cx="215" cy="230" rx="12" ry="30" fill="url(#astronautGradient)"/>
                </g>

                <!-- Floating planets -->
                <g class="animate-pulse">
                  <circle cx="80" cy="80" r="15" fill="url(#planet1Gradient)"/>
                  <circle cx="320" cy="100" r="20" fill="url(#planet2Gradient)"/>
                  <circle cx="350" cy="250" r="12" fill="url(#planet3Gradient)"/>
                </g>

                <!-- Stars -->
                <g class="animate-pulse" style="animation-delay: 0.5s;">
                  <circle cx="50" cy="50" r="2" fill="white"/>
                  <circle cx="150" cy="40" r="1.5" fill="white"/>
                  <circle cx="300" cy="60" r="2" fill="white"/>
                  <circle cx="370" cy="180" r="1.5" fill="white"/>
                  <circle cx="30" cy="200" r="2" fill="white"/>
                  <circle cx="100" cy="280" r="1.5" fill="white"/>
                </g>

                <!-- Gradients -->
                <defs>
                  <linearGradient id="astronautGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#e0e7ff"/>
                    <stop offset="100%" style="stop-color:#c7d2fe"/>
                  </linearGradient>
                  <radialGradient id="helmetGradient" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" style="stop-color:rgba(255,255,255,0.9)"/>
                    <stop offset="100%" style="stop-color:rgba(255,255,255,0.6)"/>
                  </radialGradient>
                  <linearGradient id="planet1Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#fbbf24"/>
                    <stop offset="100%" style="stop-color:#f59e0b"/>
                  </linearGradient>
                  <linearGradient id="planet2Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#ec4899"/>
                    <stop offset="100%" style="stop-color:#db2777"/>
                  </linearGradient>
                  <linearGradient id="planet3Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#06b6d4"/>
                    <stop offset="100%" style="stop-color:#0891b2"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <!-- Large 404 Text -->
            <div class="text-6xl lg:text-7xl font-black text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text mb-4 select-none">
              404
            </div>

            <!-- Error Message -->
            <h1 class="text-4xl lg:text-5xl font-bold text-white mb-4">
              Oops! Page Not Found
            </h1>
            <p class="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed mb-8">
              The page you're looking for seems to have vanished into the digital void.
            </p>

            <!-- Navigation Buttons -->
            <div class="flex flex-wrap gap-4 justify-center">
              <WrButton
                @click="handleErrorCleared"
              >
                Go Home
              </WrButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 500 Error -->
    <div v-else-if="is500">
      <div class="min-h-screen bg-gradient-to-br from-red-900 via-orange-900 to-yellow-900 flex items-center justify-center">
        <div class="text-center text-white">
          <!-- 500 Illustration -->
          <div class="mb-8 flex justify-center">
            <svg class="w-80 h-64 lg:w-96 lg:h-80" viewBox="0 0 400 320" fill="none" xmlns="http://www.w3.org/2000/svg">
              <!-- Server rack with error -->
              <g>
                <!-- Server rack base -->
                <rect x="150" y="100" width="100" height="150" rx="8" fill="url(#serverGradient)" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>

                <!-- Server units -->
                <rect x="160" y="110" width="80" height="20" rx="4" fill="rgba(255,255,255,0.1)"/>
                <rect x="160" y="140" width="80" height="20" rx="4" fill="rgba(255,255,255,0.1)"/>
                <rect x="160" y="170" width="80" height="20" rx="4" fill="rgba(255,255,255,0.1)"/>
                <rect x="160" y="200" width="80" height="20" rx="4" fill="rgba(255,255,255,0.1)"/>

                <!-- Error indicators (blinking red lights) -->
                <g class="animate-pulse">
                  <circle cx="170" cy="120" r="3" fill="#ef4444"/>
                  <circle cx="170" cy="150" r="3" fill="#ef4444"/>
                  <circle cx="170" cy="180" r="3" fill="#ef4444"/>
                  <circle cx="170" cy="210" r="3" fill="#ef4444"/>
                </g>

                <!-- Smoke/steam coming from server -->
                <g style="animation-duration: 2s;">
                  <ellipse cx="200" cy="85" rx="8" ry="12" fill="rgba(255,255,255,0.3)"/>
                  <ellipse cx="195" cy="70" rx="6" ry="10" fill="rgba(255,255,255,0.2)"/>
                  <ellipse cx="205" cy="55" rx="4" ry="8" fill="rgba(255,255,255,0.1)"/>
                </g>

                <!-- Warning symbols -->
                <g class="animate-pulse" style="animation-delay: 0.3s;">
                  <!-- Left warning -->
                  <polygon points="100,180 110,160 120,180" fill="#fbbf24"/>
                  <text x="110" y="175" text-anchor="middle" fill="#000" font-size="12" font-weight="bold">!</text>

                  <!-- Right warning -->
                  <polygon points="280,180 290,160 300,180" fill="#fbbf24"/>
                  <text x="290" y="175" text-anchor="middle" fill="#000" font-size="12" font-weight="bold">!</text>
                </g>

                <!-- Cables -->
                <path d="M150 230 Q130 240 120 260" stroke="rgba(255,255,255,0.4)" stroke-width="3" fill="none"/>
                <path d="M250 230 Q270 240 280 260" stroke="rgba(255,255,255,0.4)" stroke-width="3" fill="none"/>
              </g>

              <defs>
                <linearGradient id="serverGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style="stop-color:#374151"/>
                  <stop offset="100%" style="stop-color:#1f2937"/>
                </linearGradient>
              </defs>
            </svg>
          </div>

          <h1 class="text-6xl font-bold mb-4">500</h1>
          <p class="text-xl mb-8">Internal Server Error</p>
          <WrButton container-class="mx-auto"
            @click="handleErrorCleared"
          >
            Try Again
          </WrButton>
        </div>
      </div>
    </div>

    <!-- Generic Error -->
    <div v-else>
      <div class="min-h-screen bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center">
        <div class="text-center text-white">
          <!-- Generic Error Illustration -->
          <div class="mb-8 flex justify-center">
            <svg class="w-80 h-64 lg:w-96 lg:h-80" viewBox="0 0 400 320" fill="none" xmlns="http://www.w3.org/2000/svg">
              <!-- Broken robot -->
              <g>
                <!-- Robot head -->
                <rect x="170" y="80" width="60" height="50" rx="8" fill="url(#robotGradient)" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>

                <!-- Eyes (one working, one broken) -->
                <circle cx="185" cy="100" r="6" fill="#10b981"/>
                <g class="animate-pulse">
                  <circle cx="215" cy="100" r="6" fill="#ef4444"/>
                  <path d="M210 95 L220 105 M220 95 L210 105" stroke="#ef4444" stroke-width="2"/>
                </g>

                <!-- Robot body -->
                <rect x="160" y="130" width="80" height="80" rx="8" fill="url(#robotGradient)" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>

                <!-- Control panel -->
                <rect x="175" y="145" width="50" height="30" rx="4" fill="rgba(0,0,0,0.3)"/>
                <circle cx="185" cy="160" r="3" fill="#10b981"/>
                <circle cx="200" cy="160" r="3" fill="#fbbf24"/>
                <circle cx="215" cy="160" r="3" fill="#ef4444"/>

                <!-- Arms (one hanging loose) -->
                <rect x="130" y="140" width="25" height="12" rx="6" fill="url(#robotGradient)"/>
                <g class="animate-swing" style="transform-origin: 245px 146px;">
                  <rect x="245" y="140" width="25" height="12" rx="6" fill="url(#robotGradient)"/>
                </g>

                <!-- Legs -->
                <rect x="175" y="210" width="15" height="40" rx="6" fill="url(#robotGradient)"/>
                <rect x="210" y="210" width="15" height="40" rx="6" fill="url(#robotGradient)"/>

                <!-- Sparks/glitches -->
                <g class="animate-pulse">
                  <circle cx="250" cy="120" r="2" fill="#fbbf24"/>
                  <circle cx="260" cy="130" r="1.5" fill="#fbbf24"/>
                  <circle cx="255" cy="140" r="1" fill="#fbbf24"/>
                </g>
              </g>

              <defs>
                <linearGradient id="robotGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style="stop-color:#9ca3af"/>
                  <stop offset="100%" style="stop-color:#6b7280"/>
                </linearGradient>
              </defs>
            </svg>
          </div>

          <h1 class="text-6xl font-bold mb-4">{{ error?.statusCode || 'Error' }}</h1>
          <p class="text-xl mb-8">{{ error?.statusMessage || 'Something went wrong' }}</p>
          <WrButton
            @click="handleErrorCleared"
            container-class="mx-auto"
          >
            Go Back
          </WrButton>
        </div>
      </div>
    </div>
  </div>
  <WrFooter />
</template>
